import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface RainEffectProps {
  intensity?: number;      // 0-1
  particleCount?: number;
  areaSize?: [number, number]; // width, depth
  color?: string;
  windStrength?: number;
  onDrop?: () => void; // Callback for rain drop sounds
}

export function RainEffect({
  intensity = 0.5,
  particleCount = 1000,
  areaSize = [20, 20],
  color = '#a0c0e0',
  windStrength = 0.3,
  onDrop,
}: RainEffectProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const matricesRef = useRef<THREE.Matrix4[]>([]);
  const velocitiesRef = useRef<{ y: number; wind: number }[]>([]);

  // Timer for rain drop sounds - much less frequent to avoid mobile crashes
  const lastDropSoundTime = useRef(0);
  const nextDropDelay = useRef(0.8 + Math.random() * 1.2); // 800-2000ms between drops (mobile-friendly)

  // Initialize rain drops
  const actualCount = Math.floor(particleCount * intensity);

  useMemo(() => {
    matricesRef.current = [];
    velocitiesRef.current = [];

    for (let i = 0; i < actualCount; i++) {
      const matrix = new THREE.Matrix4();
      const x = (Math.random() - 0.5) * areaSize[0];
      const y = Math.random() * 15 + 5; // Height range
      const z = (Math.random() - 0.5) * areaSize[1];

      matrix.setPosition(x, y, z);
      matricesRef.current.push(matrix);

      velocitiesRef.current.push({
        y: 8 + Math.random() * 4, // Fall speed
        wind: (Math.random() - 0.5) * windStrength,
      });
    }
  }, [actualCount, areaSize[0], areaSize[1], windStrength]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;

    const [width, depth] = areaSize;
    const time = state.clock.elapsedTime;

    // Play rain drop sounds at random intervals (adjusted by intensity)
    // Reduced frequency to avoid mobile crashes - just ambient drops
    if (onDrop && time - lastDropSoundTime.current > nextDropDelay.current) {
      onDrop();
      lastDropSoundTime.current = time;
      // Base delay between 0.5-1.5 seconds, scaled by intensity
      // Higher intensity = slightly more frequent, but still gentle
      const baseDelay = 0.5 + (1 - intensity) * 1.0; // 0.5s at max intensity, 1.5s at min
      nextDropDelay.current = baseDelay + Math.random() * baseDelay; // Add randomness
    }

    for (let i = 0; i < matricesRef.current.length; i++) {
      const matrix = matricesRef.current[i];
      const vel = velocitiesRef.current[i];

      // Extract current position
      const pos = new THREE.Vector3();
      pos.setFromMatrixPosition(matrix);

      // Update position
      pos.y -= vel.y * delta;
      pos.x += vel.wind * delta;

      // Reset when hitting ground or going out of bounds
      if (pos.y < 0) {
        pos.y = 15 + Math.random() * 5;
        pos.x = (Math.random() - 0.5) * width;
        pos.z = (Math.random() - 0.5) * depth;
      }

      // Wrap around horizontally
      if (pos.x > width / 2) pos.x = -width / 2;
      if (pos.x < -width / 2) pos.x = width / 2;

      matrix.setPosition(pos.x, pos.y, pos.z);
      meshRef.current!.setMatrixAt(i, matrix);
    }

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  if (actualCount === 0) return null;

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, actualCount]}>
      {/* Elongated shape for rain drops */}
      <cylinderGeometry args={[0.01, 0.01, 0.3, 4]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.6 * intensity}
      />
    </instancedMesh>
  );
}
